// frontend/src/pages/ContentViewPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContentItem, updateContent } from '../services/api';
import { Copy, ArrowLeft, Edit3, Save, X, Check, Loader, Download } from 'lucide-react';

const ContentViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadContent = async () => {
    try {
      setError(null);
      const contentRes = await getContentItem(id);
      setContent(contentRes.data);
      setEditText(contentRes.data.content);
      setEditTitle(contentRes.data.title || '');
    } catch (err) {
      console.error('Failed to load content', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    const textToCopy = editing ? editText : content.content;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers or non-HTTPS
        const textarea = document.createElement('textarea');
        textarea.value = textToCopy;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      // Last resort fallback
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };


  const handleExportDocx = () => {
  const token = localStorage.getItem('token');
  window.location.href = `${process.env.REACT_APP_API_URL || ''}/api/content/${id}/export/docx?token=${token}`;
};


  const handleSave = async () => {
    setSaving(true);
    try {
      await updateContent(id, { content: editText, title: editTitle });
      setContent({ ...content, content: editText, title: editTitle });
      setEditing(false);
    } catch (err) {
      console.error('Failed to save content', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = () => {
    setEditText(content.content);
    setEditTitle(content.title || '');
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditText(content.content);
    setEditTitle(content.title || '');
  };

  const formatContentType = (type) => {
    const labels = {
      linkedin_post: 'LinkedIn Post',
      white_paper: 'White Paper',
      report: 'Report',
      article: 'Article',
      policy_brief: 'Policy Brief'
    };
    return labels[type] || type;
  };

  const getContentTypeColor = (type) => {
    const colors = {
      linkedin_post: 'bg-blue-50 text-blue-700 border-blue-200',
      white_paper: 'bg-purple-50 text-purple-700 border-purple-200',
      report: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      article: 'bg-amber-50 text-amber-700 border-amber-200',
      policy_brief: 'bg-rose-50 text-rose-700 border-rose-200'
    };
    return colors[type] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const wordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="text-center py-16">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md mx-auto p-8">
          <p className="text-gray-500 mb-4">{error || 'Content not found'}</p>
          <button
            onClick={() => navigate('/content')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-brand-mid hover:text-brand-dark transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Content Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Navigation Bar */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/content')}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand-dark transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Content Library
        </button>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            {/* Title & Meta */}
            <div className="flex-1 min-w-0">
              {editing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full text-xl font-bold text-brand-dark bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-mid focus:border-transparent"
                  placeholder="Content title..."
                />
              ) : (
                <h1 className="text-xl font-bold text-brand-dark truncate">
                  {content.title || 'Untitled'}
                </h1>
              )}

              <div className="flex items-center gap-3 mt-3">
                <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${getContentTypeColor(content.content_type)}`}>
                  {formatContentType(content.content_type)}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(content.created_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  })}
                  {' at '}
                  {new Date(content.created_at).toLocaleTimeString('en-GB', {
                    hour: '2-digit', minute: '2-digit'
                  })}
                </span>
                <span className="text-xs text-gray-400">
                  {wordCount(content.content).toLocaleString()} words
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {editing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <X size={15} />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <Save size={15} />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleStartEdit}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Edit3 size={15} />
                    Edit
                  </button>
                  <button
                    onClick={handleCopy}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white rounded-lg transition-all ${
                      copied
                        ? 'bg-green-600'
                        : 'bg-brand-dark hover:bg-brand-mid'
                    }`}
                  >
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                    {copied ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                  <button
                    onClick={handleExportDocx}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <Download size={15} />
                    Export Word
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-8">
          {editing ? (
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full min-h-[500px] p-4 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid focus:border-transparent text-sm leading-relaxed resize-y"
              placeholder="Content..."
            />
          ) : (
            <div className="prose prose-gray max-w-none whitespace-pre-wrap text-gray-700 leading-relaxed text-[15px]">
              {content.content}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {wordCount(editing ? editText : content.content).toLocaleString()} words
          </span>
          {!editing && (
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-brand-mid transition-colors"
            >
              <Copy size={13} />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentViewPage;